import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ComicCard } from '../components/ComicCard';
import { useMyComics, useMyStories } from '../hooks/useComics';
import { useAuthStore } from '../stores/auth.store';
import { useUILocale } from '../hooks/useUILocale';
import { withLocale } from '../locale';
import type { StoryListItem } from '@storychain/shared';

type Tab = 'stories' | 'comics';

const MyLibraryPage: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const uiLocale = useUILocale();
  const [tab, setTab] = useState<Tab>('stories');

  const stories = useMyStories(isAuthenticated && tab === 'stories');
  const comics = useMyComics(isAuthenticated && tab === 'comics');

  if (!isAuthenticated) {
    return <Navigate to={withLocale(uiLocale, '/login')} replace />;
  }

  const tabs: { value: Tab; label: string }[] = [
    { value: 'stories', label: 'Stories' },
    { value: 'comics', label: 'Comics' },
  ];

  return (
    <Layout wide>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Library</h1>
          <p className="text-gray-500">Everything you've created on StoryChain.</p>
        </div>

        <div className="border-b border-gray-200">
          <div className="flex gap-1">
            {tabs.map((t) => (
              <button
                key={t.value}
                onClick={() => setTab(t.value)}
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                  tab === t.value
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'stories' && (
          <StoriesList
            loading={stories.isLoading}
            stories={stories.data?.stories || []}
          />
        )}
        {tab === 'comics' && (
          <>
            {comics.isLoading ? (
              <GridSkeleton />
            ) : (comics.data?.comics?.length ?? 0) === 0 ? (
              <EmptyState message="No comics yet. Finish an adventure in the mobile app to generate one!" />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {comics.data!.comics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

const GridSkeleton: React.FC = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="aspect-[3/4] bg-white rounded-xl animate-pulse" />
    ))}
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-center py-16 bg-white rounded-xl text-gray-500">{message}</div>
);

const StoriesList: React.FC<{ loading: boolean; stories: StoryListItem[] }> = ({ loading, stories }) => {
  const uiLocale = useUILocale();
  if (loading) return <GridSkeleton />;
  if (stories.length === 0) {
    return <EmptyState message="No stories yet. Start one in the StoryChain app!" />;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {stories.map((s) => {
        const href =
          s.status === 'completed'
            ? withLocale(uiLocale, `/story/${s.id}/completed`)
            : withLocale(uiLocale, `/story/${s.id}/write`);
        const cover = s.coverImageMedium || s.coverImage;
        return (
          <Link
            key={s.id}
            to={href}
            className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition flex"
          >
            <div className="w-24 h-24 flex-shrink-0 bg-gray-100">
              {cover ? (
                <img src={cover} alt={s.title || 'Story'} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-3xl">📖</div>
              )}
            </div>
            <div className="flex-1 min-w-0 p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full capitalize">{s.genre}</span>
                <StatusBadge status={s.status} isMyTurn={s.isMyTurn} />
                {(s.reactionCount || 0) > 0 && (
                  <span className="text-xs text-gray-500 tabular-nums">🎭 {s.reactionCount}</span>
                )}
              </div>
              <h3 className="font-semibold text-gray-900 line-clamp-1">
                {s.title || `Story ${s.code}`}
              </h3>
              <p className="text-xs text-gray-500 line-clamp-1">
                {s.status === 'completed'
                  ? `${s.participantsCount} ${s.participantsCount === 1 ? 'writer' : 'writers'}`
                  : `${s.participantsCount}/${s.maxParticipants} players · turn ${s.currentTurn}`}
              </p>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  expired: 'bg-gray-100 text-gray-600',
};

const StatusBadge: React.FC<{ status: string; isMyTurn: boolean }> = ({ status, isMyTurn }) => {
  if (status === 'completed') return null;
  if (isMyTurn && status === 'active') {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Your turn</span>;
  }
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
};

export default MyLibraryPage;
